import { Box, Button, CardInfo, Text } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import isEqual from 'lodash/isEqual'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import {
  IBulkProcessItemTimeRecord,
  useBulkProcessProjectTimeRecord,
} from 'modules/project_management/hooks/projectTimeRecord/useBulkProcessProjectTimeRecord'
import { type ProjectTimeRecord } from 'modules/project_management/hooks/projectTimeRecord/useGetProjectTimeRecords'
import { IUseGetEmployeeProjectsTrackingMonthData } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'

import AssignProjectToShiftForm from './AssignProjectToShiftForm'
import { IProjectValues, IShiftWithProjectValues, IShiftWithProjectValuesHandler } from './types'

interface IAddProjectTimeRecordProps {
  shifts: TimesheetPageShiftFragment[]
  onClose: () => void
  projectsData: IUseGetEmployeeProjectsTrackingMonthData
  date: moment.Moment
}

export interface IShiftWithProject {
  [key: number]: ProjectTimeRecord | null | undefined
}

const AddProjectTimeRecordModal = ({
  shifts,
  onClose,
  projectsData,
  date,
}: IAddProjectTimeRecordProps) => {
  const { bulkProcessProjectTimeRecord, loading: bulkProcessLoading } =
    useBulkProcessProjectTimeRecord()

  let shiftsWithProject: IShiftWithProject = {}

  // Iterate over the projectTimeRecords and create an object with the shiftId as key and the project as value
  projectsData.timeRecords.map((tr: ProjectTimeRecord) => {
    if (!tr?.attendanceShiftId || !tr?.projectWorker?.imputableProject) return shiftsWithProject
    const projectShift: IShiftWithProject = {
      [tr.attendanceShiftId]: { ...tr },
    }

    shiftsWithProject = { ...shiftsWithProject, ...projectShift }
    return shiftsWithProject
  })

  let shiftWithProjectValues: IShiftWithProjectValues = {}
  let shiftWithInitialProjectValues: IShiftWithProjectValues = {}

  shifts.map((shift) => {
    const associatedTimeRecord = shiftsWithProject[shift.id]

    const newShiftWithProjectValue: IShiftWithProjectValues = {
      [shift.id]: {
        projectWorkerId: associatedTimeRecord?.projectWorker?.id || undefined,
        subprojectId: associatedTimeRecord?.subproject?.id || undefined,
        shiftId: shift.id,
        timeRecord: associatedTimeRecord || undefined,
      },
    }

    shiftWithProjectValues = { ...shiftWithProjectValues, ...newShiftWithProjectValue }
    shiftWithInitialProjectValues = {
      ...shiftWithInitialProjectValues,
      ...newShiftWithProjectValue,
    }
  })

  const shiftWithProjectValuesHandler = ({
    shiftId,
    projectWorkerId,
    subprojectId,
  }: IShiftWithProjectValuesHandler) => {
    const currentValues = shiftWithProjectValues[shiftId]

    if (projectWorkerId) {
      shiftWithProjectValues[shiftId] = {
        ...currentValues,
        projectWorkerId: projectWorkerId,
        subprojectId: undefined,
        shiftId: shiftId,
      }
    }

    if (subprojectId) {
      shiftWithProjectValues[shiftId] = {
        ...currentValues,
        subprojectId: subprojectId,
        shiftId: shiftId,
      }
    }

    if (!projectWorkerId && !subprojectId) {
      shiftWithProjectValues[shiftId] = {
        ...currentValues,
        projectWorkerId: undefined,
        subprojectId: undefined,
        shiftId: shiftId,
      }
    }
  }

  const onSubmit = async () => {
    if (isEqual(shiftWithProjectValues, shiftWithInitialProjectValues)) {
      onClose()

      return
    }

    const bulkProcessItems: IBulkProcessItemTimeRecord[] = []

    shifts.forEach((shift) => {
      const projectValues = shiftWithProjectValues[shift.id] as IProjectValues
      const initialValues = shiftWithInitialProjectValues[shift.id] as IProjectValues

      if (isEqual(projectValues, initialValues)) {
        return
      }

      bulkProcessItems.push({
        timeRecordId: projectValues.timeRecord?.id,
        attendanceShiftId: projectValues.shiftId,
        projectWorkerId: projectValues.projectWorkerId,
        subprojectId: projectValues.subprojectId,
      })
    })

    await asyncAction(() => bulkProcessProjectTimeRecord({ items: bulkProcessItems }), {
      successFlash: true,
      successMessage: i18n.t('common.saved_successfully'),
      errorFlash: true,
    })

    track(trackingEvents.projectManagement.projectTimeRecords.recordProjectTimeSubmit, {
      device_name: 'Desktop',
    })

    await projectsData.refetchEmployeeTimeRecords()

    onClose()
  }

  const disabledSubmitButton = projectsData.fetchingTimeRecords || bulkProcessLoading

  return (
    <Modal
      illustration="calendar-clock"
      title={i18n.t('clock_in.projects.modal.title')}
      description={i18n.t('clock_in.projects.modal.description', {
        day: date.format('Do'),
        month: date.format('MMMM'),
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={disabledSubmitButton} submit>
          {i18n.t('clock_in.projects.edit.title')}
        </Button>
      }
    >
      <Box gap="s16">
        {projectsData.hasProjectsToTrack &&
          shifts
            .filter((shift) => shift.workable)
            .map((shift) => {
              let label = `${i18n.t(
                'clock_in.observations.modal.field_title'
              )} ${AttendanceShiftUtils.clockInTimeStr(shift)}`

              if (shift.clockOut) {
                label = `${label} - ${AttendanceShiftUtils.clockOutTimeStr(shift)}`
              }

              return (
                <CardInfo key={shift.id}>
                  <Box gap="s16">
                    <Text size="300" weight="medium" key={`label-${shift.id}`}>
                      {label}
                    </Text>
                    <AssignProjectToShiftForm
                      key={`project-${shift.id}`}
                      shiftWithProjectTimeRecord={shiftsWithProject[shift.id] || null}
                      shiftWithProjectValues={shiftWithProjectValues[shift.id] as IProjectValues}
                      shiftWithProjectValuesHandler={shiftWithProjectValuesHandler}
                      projectsAssigned={projectsData.projectsAssigned}
                    />
                  </Box>
                </CardInfo>
              )
            })}
      </Box>
    </Modal>
  )
}

export default AddProjectTimeRecordModal
